/**
 * <s-product-gallery> — Brings the selected variant's image to the front of the PDP gallery.
 *
 * Shows only the selected variant's assigned image (plus unassigned media such as mockups).
 * Listens for variant option `change` events inside the closest [data-product-page] ancestor
 * (the product grid that also holds <s-variant-picker>) and moves the gallery item whose
 * media is assigned to the selected variant into the first position, then scrolls the
 * gallery back to its start so the buyer sees it.
 *
 * Required markup:
 *   [data-product-gallery]                         — the gallery track (child of this element).
 *   [data-media-id]                                — on each gallery item.
 *   script[type="application/json"][data-variant-media] — { "<variantId>": "<mediaId>" }.
 *
 * Optional dataset (on <s-product-gallery>):
 *   data-initial-media-id — media to bring forward on connect (deep-linked ?variant=).
 */
class SProductGallery extends HTMLElement {
  #ac;
  #variantMedia = {};

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    const mapScript = this.querySelector('script[type="application/json"][data-variant-media]');
    try {
      this.#variantMedia = JSON.parse(mapScript?.textContent || '{}');
    } catch (error) {
      console.error('[s-product-gallery] Failed to parse variant media map:', error);
    }

    if (this.dataset.initialMediaId) {
      this.#bringToFront(this.dataset.initialMediaId, false);
    }

    const scope = this.closest('[data-product-page]');
    scope?.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLElement)) return;
      const variantId = input.dataset.variantId
        || input.selectedOptions?.[0]?.dataset?.variantId;
      if (!variantId) return;

      const mediaId = this.#variantMedia[variantId];
      if (mediaId) this.#bringToFront(mediaId, true);
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #bringToFront(mediaId, reveal) {
    const track = this.querySelector('[data-product-gallery]');
    const item = track?.querySelector(`[data-media-id="${CSS.escape(String(mediaId))}"]`);
    if (!track || !item) return;

    // Hide images assigned to other variants; media not assigned to any variant stays visible.
    const variantMediaIds = new Set(Object.values(this.#variantMedia).map(String));
    for (const galleryItem of track.querySelectorAll('[data-media-id]')) {
      const id = galleryItem.dataset.mediaId;
      galleryItem.hidden = variantMediaIds.has(id) && id !== String(mediaId);
    }

    if (track.firstElementChild !== item) track.prepend(item);

    const lazyImage = item.querySelector('img[loading="lazy"]');
    if (lazyImage) lazyImage.loading = 'eager';

    if (!reveal) return;

    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    track.scrollTo({ left: 0, behavior });

    if (track.getBoundingClientRect().top < 0) {
      track.scrollIntoView({ block: 'start', behavior });
    }
  }
}
customElements.define('s-product-gallery', SProductGallery);
